import {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type MouseEvent
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
// The whole log at once - the single version in the row menu keeps the
// plain bin.
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RestoreIcon from '@mui/icons-material/Restore';
import { parseStringArray, type HistoryVersion } from '@ast-data/shared';
import { useHistory } from './useHistory';
import {
    otherChangedKeys,
    summarizeValue,
    type ValueSummary
} from './historyChanges';
import { useOnlineStatus } from '../sync/useOnlineStatus';
import { useActorLabel } from '../users/useActorLabel';
import DiffView from '../../components/DiffView';
import { diffStats } from '../../components/diffStats';
import ConfirmDialog from '../../components/ConfirmDialog';
import GridCrudToolbar from '../../components/GridCrudToolbar';
import LoadErrorAlert from '../../components/LoadErrorAlert';
import { formatDateTime } from '../../utils/formatDate';
import { HISTORY_GRID_SX } from './historyGridSx';
import {
    DataGridPro,
    GridActionsCellItem,
    GRID_DETAIL_PANEL_TOGGLE_FIELD,
    dateTimeColumn,
    useGridApiRef,
    type GridColDef,
    type GridRowParams
} from '../../lib/dataGrid';

export interface HistoryField {
    key: string;
    label: string;
    /**
     * Further snapshot fields this ONE field is spread over — a reference set the
     * record stores in several arrays but the form shows as one control
     * ("identisch mit" = Planum ids + Profil ids). Their contents are pooled.
     */
    keys?: string[];
    isHtml?: boolean;
    fallback?: string;
    /**
     * Marks this field as a set of reference ids (e.g. a project's member ids).
     * Changes are shown as added/removed entries with the id resolved to a label
     * via this function, instead of a text diff.
     */
    resolveId?: (id: string) => string;
    /**
     * Renders the raw snapshot value (e.g. an embedded object array) to readable
     * text before diffing. Receives the raw value at `version[key]`.
     */
    format?: (rawValue: unknown) => string;
}

function fieldKeys(field: HistoryField): string[] {
    return field.keys ?? [field.key];
}

function idArray(version: HistoryVersion, field: HistoryField): string[] {
    return fieldKeys(field).flatMap((key) => parseStringArray(version[key]));
}

interface HistoryDialogProps {
    open: boolean;
    onClose: () => void;
    /** Entity API root, e.g. '/api/entries' or '/api/projects'. */
    apiBase: string;
    entityId: string;
    /** Snapshot field used as each version's list title. */
    primaryField: string;
    /**
     * The title from the whole version, where one field does not name the
     * record (a Fund is Fundzettel- plus Unterfundzettelnummer: "123_2").
     */
    primaryLabel?: (version: HistoryVersion) => string;
    fields: HistoryField[];
    /** Optional snapshot field appended to the timestamp subtitle. */
    subtitleField?: string;
    /**
     * i18n namespace for the fields this dialog does NOT diff, so the "weitere
     * Änderungen" block can still name them (e.g. 'planum.field'). Falls back to
     * the generic `field.*` namespace and finally to the raw field name.
     */
    labelPrefix?: string;
    title?: string;
    onRestored?: () => void;
    /**
     * Hide versions that didn't change any tracked `field` versus their
     * predecessor (plus the initial version when it has no tracked content). Use
     * when the tracked fields are a subset of the entity, e.g. the phases-only
     * history over the project's snapshots. Such a dialog also leaves the
     * untracked changes unmentioned: what else the project changed belongs in
     * the project's own history, not in the phase log.
     */
    changesOnly?: boolean;
    /**
     * Hide the Restore action. Restore reverts the WHOLE entity snapshot, so it's
     * misleading on a dialog scoped to a subset of fields (e.g. phases only).
     */
    hideRestore?: boolean;
    /**
     * A history that can only be READ — no restore, no deleting versions, no
     * clearing. For entities the app doesn't author (a `user`, owned by the
     * Synology NAS): the server offers the GET routes alone, so the buttons
     * would only produce 404s.
     */
    readOnly?: boolean;
    /**
     * The ONE version this dialog was opened about, marked and opened for the
     * reader (`historyId`).
     *
     * A change-log row means one particular version, but the dialog it opens is
     * the record's whole history, and a project changed twice since gives no
     * clue which of the three lines the row was. Every other caller opens the
     * history of a RECORD and leaves it unset.
     */
    highlightVersionId?: string;
}

function fieldValue(
    version: HistoryVersion,
    key: string,
    fallback = ''
): string {
    const value = version[key];
    if (value === undefined || value === null || value === '') {
        return fallback;
    }
    // A plain array field (a profile's roles) reads as a list, not as the
    // comma-jammed string `String([...])` would produce.
    if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : fallback;
    }
    return String(value);
}

function fieldText(version: HistoryVersion, field: HistoryField): string {
    if (field.format) {
        return field.format(version[field.key]);
    }
    return fieldValue(version, field.key, field.fallback);
}

/** One version as a grid row, with everything the columns show precomputed. */
interface VersionRow {
    id: string;
    version: HistoryVersion;
    /** The true chronological predecessor; null for the very first version. */
    previousVersion: HistoryVersion | null;
    title: string;
    savedAt: string;
    actor: string;
    /** What this version changed, as field labels — the "Änderungen" column. */
    summary: string;
    isLatest: boolean;
    /** The version the caller came FROM — see `highlightVersionId`. */
    isHighlighted: boolean;
}

export default function HistoryDialog({
    open,
    onClose,
    apiBase,
    entityId,
    primaryField,
    primaryLabel,
    fields,
    subtitleField,
    labelPrefix,
    title,
    onRestored,
    changesOnly,
    hideRestore,
    readOnly,
    highlightVersionId
}: HistoryDialogProps) {
    const intl = useIntl();
    const {
        versions,
        loading,
        error,
        loadFailure,
        refresh,
        clearHistory,
        deleteVersion,
        restoreVersion
    } = useHistory(apiBase, open ? entityId : undefined);
    const online = useOnlineStatus();
    const [restoring, setRestoring] = useState(false);
    const apiRef = useGridApiRef();

    // Every destructive action asks first, and asks in the app's own dialog.
    // Each state holds what it is about, which is what the confirmation reads
    // back.
    const [restoreTarget, setRestoreTarget] = useState<HistoryVersion | null>(
        null
    );
    const [deleteTarget, setDeleteTarget] = useState<VersionRow | null>(null);
    const [clearOpen, setClearOpen] = useState(false);

    const actorLabel = useActorLabel();

    async function handleRestore(version: HistoryVersion) {
        setRestoreTarget(null);
        setRestoring(true);
        try {
            const restored = await restoreVersion(version.historyId);
            if (restored) {
                onRestored?.();
            }
        } finally {
            setRestoring(false);
        }
    }

    async function handleDelete(historyId: string) {
        setDeleteTarget(null);
        await deleteVersion(historyId);
    }

    async function handleClearAll() {
        setClearOpen(false);
        await clearHistory();
    }

    const changedFields = useCallback(
        (version: HistoryVersion, previousVersion: HistoryVersion) =>
            fields.filter((field) => {
                if (field.resolveId) {
                    const current = [...idArray(version, field)].sort();
                    const previous = [
                        ...idArray(previousVersion, field)
                    ].sort();
                    return (
                        current.length !== previous.length ||
                        current.some((id, index) => id !== previous[index])
                    );
                }
                return (
                    fieldText(version, field) !==
                    fieldText(previousVersion, field)
                );
            }),
        [fields]
    );

    // Everything the dialog diffs itself, so the REST can be found and named
    // rather than silently swallowed (see historyChanges).
    const trackedKeys = useMemo(() => fields.flatMap(fieldKeys), [fields]);

    // The changed fields this dialog does not diff — a deletion, a reference
    // array, the sketch's north arrow. Empty for a scoped dialog.
    const otherChanges = useCallback(
        (version: HistoryVersion, previousVersion: HistoryVersion | null) =>
            previousVersion && !changesOnly
                ? otherChangedKeys(version, previousVersion, trackedKeys)
                : [],
        [changesOnly, trackedKeys]
    );

    // What to call a snapshot field the dialog doesn't list: the entity's own
    // namespace first, then the generic one, and the bare field name rather than
    // a MISSING_TRANSLATION for the plumbing fields that have no label anywhere.
    const otherFieldLabel = useCallback(
        (key: string) => {
            const scopedId = labelPrefix ? `${labelPrefix}.${key}` : '';
            if (scopedId && intl.messages[scopedId]) {
                return intl.formatMessage({ id: scopedId });
            }
            const genericId = `field.${key}`;
            if (intl.messages[genericId]) {
                return intl.formatMessage({ id: genericId });
            }
            return key;
        },
        [intl, labelPrefix]
    );

    /** A value the dialog can't diff, worded rather than printed. */
    const summaryText = useCallback(
        (summary: ValueSummary): string => {
            switch (summary.kind) {
                case 'empty':
                    return intl.formatMessage({ id: 'history.value.empty' });
                case 'text':
                    return summary.text;
                case 'boolean':
                    return intl.formatMessage({
                        id: summary.value ? 'common.yes' : 'common.no'
                    });
                case 'items':
                    return intl.formatMessage(
                        { id: 'history.value.items' },
                        { count: summary.count }
                    );
                case 'chars':
                    return intl.formatMessage(
                        { id: 'history.value.chars' },
                        { count: summary.count }
                    );
                default:
                    return intl.formatMessage({ id: 'history.value.changed' });
            }
        },
        [intl]
    );

    /** One diffed field: a resolved reference set, or a word-level text diff. */
    function renderField(
        version: HistoryVersion,
        previousVersion: HistoryVersion,
        field: HistoryField
    ) {
        // Reference-id set (e.g. project members): show resolved +added/−removed.
        if (field.resolveId) {
            const resolve = field.resolveId;
            const oldIds = idArray(previousVersion, field);
            const newIds = idArray(version, field);
            const oldSet = new Set(oldIds);
            const newSet = new Set(newIds);
            const added = newIds.filter((id) => !oldSet.has(id));
            const removed = oldIds.filter((id) => !newSet.has(id));
            return (
                <Box key={field.key} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        {field.label}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {added.map((id) => (
                            <Box
                                key={`+${id}`}
                                component="span"
                                sx={{ color: 'success.main' }}
                            >
                                +{resolve(id)}
                            </Box>
                        ))}
                        {removed.map((id) => (
                            <Box
                                key={`-${id}`}
                                component="span"
                                sx={{
                                    color: 'error.main',
                                    textDecoration: 'line-through'
                                }}
                            >
                                −{resolve(id)}
                            </Box>
                        ))}
                    </Box>
                </Box>
            );
        }
        const oldValue = fieldText(previousVersion, field);
        const newValue = fieldText(version, field);
        const stats = diffStats(oldValue, newValue, field.isHtml);
        return (
            <Box key={field.key} sx={{ mb: 2 }}>
                <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}
                >
                    {field.label}
                    {stats.removed > 0 && (
                        <Box
                            component="span"
                            sx={{
                                color: 'error.main',
                                fontFamily: 'monospace'
                            }}
                        >
                            −{stats.removed}
                        </Box>
                    )}
                    {stats.added > 0 && (
                        <Box
                            component="span"
                            sx={{
                                color: 'success.main',
                                fontFamily: 'monospace'
                            }}
                        >
                            +{stats.added}
                        </Box>
                    )}
                </Typography>
                <DiffView
                    oldValue={oldValue}
                    newValue={newValue}
                    isHtml={field.isHtml}
                />
            </Box>
        );
    }

    // The changes OUTSIDE the diffed fields, one `before → after` line each. The
    // values are described rather than printed: these are the fields left out
    // because they read as UUIDs or as a serialized sketch, and knowing that
    // they changed is the point.
    function renderOtherChanges(
        version: HistoryVersion,
        previousVersion: HistoryVersion,
        keys: string[]
    ) {
        const gone = version._deleted === true;
        const back = previousVersion._deleted === true && !gone;
        return (
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    <FormattedMessage id="history.otherChanges" />
                </Typography>
                {(gone || back) && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: gone ? 'error.main' : 'success.main',
                            mb: 0.5
                        }}
                    >
                        <FormattedMessage
                            id={
                                gone
                                    ? 'history.recordDeleted'
                                    : 'history.recordRestored'
                            }
                        />
                    </Typography>
                )}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr',
                        columnGap: 1.5,
                        rowGap: 0.25,
                        alignItems: 'baseline'
                    }}
                >
                    {keys
                        .filter((key) => key !== '_deleted')
                        .map((key) => {
                            const before = summaryText(
                                summarizeValue(previousVersion[key])
                            );
                            const after = summaryText(
                                summarizeValue(version[key])
                            );
                            return (
                                <Fragment key={key}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: 'text.secondary' }}
                                    >
                                        {otherFieldLabel(key)}
                                    </Typography>
                                    <Typography variant="body2" component="div">
                                        {/* Two sides that WORD the same say
                                            nothing as a before → after: an
                                            embedded object is "(geändert)"
                                            either way. The field did change —
                                            the summary just cannot show how. */}
                                        {before === after ? (
                                            <Box
                                                component="span"
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                <FormattedMessage id="history.value.changed" />
                                            </Box>
                                        ) : (
                                            <>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        color: 'error.main',
                                                        textDecoration:
                                                            'line-through'
                                                    }}
                                                >
                                                    {before}
                                                </Box>
                                                {' → '}
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        color: 'success.main'
                                                    }}
                                                >
                                                    {after}
                                                </Box>
                                            </>
                                        )}
                                    </Typography>
                                </Fragment>
                            );
                        })}
                </Box>
            </Box>
        );
    }

    /** A version's expanded panel: every change it made, in full. */
    function renderChanges(
        version: HistoryVersion,
        previousVersion: HistoryVersion | null
    ) {
        if (!previousVersion) {
            return (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <FormattedMessage id="history.initialVersion" />
                </Typography>
            );
        }
        const changed = changedFields(version, previousVersion);
        const others = otherChanges(version, previousVersion);
        if (changed.length === 0 && others.length === 0) {
            return (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <FormattedMessage id="history.noChanges" />
                </Typography>
            );
        }
        return (
            <>
                {changed.map((field) =>
                    renderField(version, previousVersion, field)
                )}
                {others.length > 0 &&
                    renderOtherChanges(version, previousVersion, others)}
            </>
        );
    }

    // Pair each version with its true chronological predecessor (versions arrive
    // newest-first), then optionally drop versions with no tracked change so the
    // diff still compares against the real prior state.
    const rows = useMemo<VersionRow[]>(() => {
        const latestId = versions[0]?.historyId;
        return versions
            .map((version, index) => ({
                version,
                previousVersion:
                    index < versions.length - 1 ? versions[index + 1] : null
            }))
            .filter(({ version, previousVersion }) => {
                if (!changesOnly) {
                    return true;
                }
                return previousVersion
                    ? changedFields(version, previousVersion).length > 0
                    : fields.some(
                          (field) => fieldText(version, field).trim() !== ''
                      );
            })
            .map(({ version, previousVersion }) => {
                const deleted = version._deleted === true;
                const restored = !deleted && previousVersion?._deleted === true;
                const labels = previousVersion
                    ? [
                          ...changedFields(version, previousVersion).map(
                              (field) => field.label
                          ),
                          ...otherChanges(version, previousVersion)
                              .filter((key) => key !== '_deleted')
                              .map(otherFieldLabel)
                      ]
                    : [];
                // What the row SAYS happened. A record going away or coming back
                // is the whole story of that version; otherwise it is the list
                // of what was touched.
                const summary = !previousVersion
                    ? intl.formatMessage({ id: 'history.initialVersion' })
                    : deleted
                      ? intl.formatMessage({ id: 'history.recordDeleted' })
                      : [
                            ...(restored
                                ? [
                                      intl.formatMessage({
                                          id: 'history.recordRestored'
                                      })
                                  ]
                                : []),
                            ...labels
                        ].join(', ') ||
                        intl.formatMessage({ id: 'history.noChanges' });
                const subtitle = subtitleField
                    ? fieldValue(version, subtitleField)
                    : '';
                return {
                    id: version.historyId,
                    version,
                    previousVersion,
                    title: [
                        primaryLabel
                            ? primaryLabel(version)
                            : fieldValue(version, primaryField),
                        subtitle
                    ]
                        .filter(Boolean)
                        .join(' — '),
                    savedAt: version.savedAt,
                    actor: actorLabel(version.changedBy),
                    summary,
                    isLatest: version.historyId === latestId,
                    isHighlighted: version.historyId === highlightVersionId
                };
            });
    }, [
        versions,
        changesOnly,
        changedFields,
        otherChanges,
        otherFieldLabel,
        fields,
        intl,
        primaryField,
        primaryLabel,
        subtitleField,
        actorLabel,
        highlightVersionId
    ]);

    // …and bring it into view. A record edited often has a long list, and marked
    // but off screen is the same as unmarked. Once per target: a later scroll by
    // the reader is theirs to keep.
    const scrolledTo = useRef<string | null>(null);
    useEffect(() => {
        if (!highlightVersionId || rows.length === 0) {
            return;
        }
        if (scrolledTo.current === highlightVersionId) {
            return;
        }
        const rowIndex = rows.findIndex((row) => row.id === highlightVersionId);
        if (rowIndex < 0) {
            return;
        }
        scrolledTo.current = highlightVersionId;
        apiRef.current?.scrollToIndexes({ rowIndex });
    }, [apiRef, rows, highlightVersionId]);

    const columns = useMemo<GridColDef<VersionRow>[]>(
        () => [
            {
                field: 'title',
                headerName: intl.formatMessage({ id: 'history.column.record' }),
                flex: 1,
                minWidth: 160,
                renderCell: ({ row }) => (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            height: '100%',
                            minWidth: 0
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {row.title}
                        </Box>
                        {row.isHighlighted && (
                            <Chip
                                size="small"
                                color="primary"
                                label={intl.formatMessage({
                                    id: 'history.askedFor'
                                })}
                            />
                        )}
                        {row.isLatest && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={intl.formatMessage({
                                    id: 'history.latest'
                                })}
                            />
                        )}
                    </Box>
                )
            },
            dateTimeColumn<VersionRow>({
                field: 'savedAt',
                headerName: intl.formatMessage({
                    id: 'history.column.savedAt'
                }),
                width: 170
            }),
            {
                field: 'actor',
                headerName: intl.formatMessage({
                    id: 'history.column.changedBy'
                }),
                width: 150
            },
            {
                field: 'summary',
                headerName: intl.formatMessage({
                    id: 'history.column.changes'
                }),
                flex: 1.5,
                minWidth: 180
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: '',
                width: 80,
                /**
                 * Both actions live behind the row's menu: side by side they put
                 * two icon buttons on every row of a list that is mostly read.
                 * Neither runs before it is confirmed.
                 */
                getActions: ({ row }: GridRowParams<VersionRow>) => {
                    const actions = [];
                    if (!hideRestore && !readOnly && !row.isLatest) {
                        actions.push(
                            <GridActionsCellItem
                                key="restore"
                                icon={<RestoreIcon />}
                                label={intl.formatMessage({
                                    id: 'history.restore'
                                })}
                                disabled={restoring || !online}
                                onClick={() => setRestoreTarget(row.version)}
                                showInMenu
                            />
                        );
                    }
                    if (!readOnly) {
                        actions.push(
                            <GridActionsCellItem
                                key="delete"
                                icon={<DeleteIcon />}
                                label={intl.formatMessage({
                                    id: 'history.delete.action'
                                })}
                                disabled={!online}
                                onClick={() => setDeleteTarget(row)}
                                showInMenu
                            />
                        );
                    }
                    return actions;
                }
            }
        ],
        [intl, hideRestore, readOnly, restoring, online]
    );

    const getDetailPanelContent = useCallback(
        ({ row }: GridRowParams<VersionRow>) => (
            <Box sx={{ px: 3, py: 2 }}>
                {renderChanges(row.version, row.previousVersion)}
            </Box>
        ),
        // Same reasoning as the columns: the renderer reads the current props
        // when a panel opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fields, changesOnly, labelPrefix]
    );
    const getDetailPanelHeight = useCallback(() => 'auto' as const, []);

    // Clicking anywhere on a version opens it. The toggle chevron and the
    // actions run their own handler, and toggling on top of that would cancel it
    // out, so a click that landed in either of those cells is left alone.
    const handleRowClick = useCallback(
        ({ id }: GridRowParams<VersionRow>, event: MouseEvent) => {
            const field = (event.target as HTMLElement)
                .closest('[data-field]')
                ?.getAttribute('data-field');
            if (
                field === GRID_DETAIL_PANEL_TOGGLE_FIELD ||
                field === 'actions'
            ) {
                return;
            }
            apiRef.current?.toggleDetailPanel(id);
        },
        [apiRef]
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            // A fixed height gives the grid a viewport to virtualize into: a
            // record with hundreds of versions then renders the handful of rows
            // that are on screen instead of all of them.
            slotProps={{ paper: { sx: { height: '85vh' } } }}
        >
            <DialogTitle>
                {title ?? intl.formatMessage({ id: 'history.title' })}
            </DialogTitle>
            {/* No padding of its own: the grid runs the full width of the
                dialog, so the two read as ONE surface. Only the messages above
                it (which are cards) keep a margin. */}
            <DialogContent
                sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0
                }}
            >
                {!online && (
                    <Alert severity="info" sx={{ m: 2, mb: 0 }}>
                        <FormattedMessage id="history.offlineHint" />
                    </Alert>
                )}
                {online && loadFailure && (
                    <LoadErrorAlert
                        title={<FormattedMessage id="history.loadError" />}
                        failure={loadFailure}
                        onRetry={() => void refresh()}
                        retrying={loading}
                        sx={{ m: 2, mb: 0 }}
                    />
                )}
                {online && error && (
                    <Alert severity="error" sx={{ m: 2, mb: 0 }}>
                        {error}
                    </Alert>
                )}
                {loading ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            py: 4
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : rows.length === 0 ? (
                    <Typography sx={{ color: 'text.secondary', p: 3 }}>
                        <FormattedMessage id="history.empty" />
                    </Typography>
                ) : (
                    <Box sx={{ flex: 1, minHeight: 240 }}>
                        <DataGridPro
                            apiRef={apiRef}
                            rows={rows}
                            columns={columns}
                            disableRowSelectionOnClick
                            onRowClick={handleRowClick}
                            getRowClassName={({ row }) =>
                                row.isHighlighted ? 'history-row-asked-for' : ''
                            }
                            getDetailPanelContent={getDetailPanelContent}
                            getDetailPanelHeight={getDetailPanelHeight}
                            initialState={{
                                sorting: {
                                    sortModel: [
                                        { field: 'savedAt', sort: 'desc' }
                                    ]
                                },
                                /**
                                 * Open on the version that was asked for. The
                                 * grid first renders once the versions are in,
                                 * so the initial state is the right place for
                                 * it.
                                 */
                                detailPanel: {
                                    expandedRowIds: new Set(
                                        highlightVersionId
                                            ? [highlightVersionId]
                                            : []
                                    )
                                }
                            }}
                            showToolbar
                            slots={{ toolbar: GridCrudToolbar }}
                            slotProps={{ toolbar: { showQuickFilter: true } }}
                            sx={HISTORY_GRID_SX}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                {!readOnly && (
                    <Button
                        color="error"
                        variant="contained"
                        startIcon={<DeleteSweepIcon />}
                        disabled={versions.length === 0 || !online}
                        onClick={() => setClearOpen(true)}
                        sx={{ mr: 'auto' }}
                    >
                        <FormattedMessage id="history.clearAll" />
                    </Button>
                )}
                <Button
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={onClose}
                >
                    <FormattedMessage id="common.close" />
                </Button>
            </DialogActions>

            <ConfirmDialog
                open={!!restoreTarget}
                title={<FormattedMessage id="history.restore.title" />}
                message={
                    <FormattedMessage
                        id="history.restore.confirm"
                        values={{
                            time: formatDateTime(restoreTarget?.savedAt ?? '')
                        }}
                    />
                }
                confirmLabel={<FormattedMessage id="history.restore.action" />}
                onConfirm={() => {
                    if (restoreTarget) {
                        void handleRestore(restoreTarget);
                    }
                }}
                onCancel={() => setRestoreTarget(null)}
            />
            <ConfirmDialog
                open={!!deleteTarget}
                title={<FormattedMessage id="history.delete.title" />}
                message={
                    <FormattedMessage
                        id="history.delete.confirm"
                        values={{
                            time: formatDateTime(deleteTarget?.savedAt ?? '')
                        }}
                    />
                }
                confirmColor="error"
                onConfirm={() => {
                    if (deleteTarget) {
                        void handleDelete(deleteTarget.id);
                    }
                }}
                onCancel={() => setDeleteTarget(null)}
            />
            <ConfirmDialog
                open={clearOpen}
                title={<FormattedMessage id="history.clearAll" />}
                message={<FormattedMessage id="history.clearConfirm" />}
                confirmColor="error"
                onConfirm={() => void handleClearAll()}
                onCancel={() => setClearOpen(false)}
            />
        </Dialog>
    );
}
